import React, {
  ChangeEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import cx from 'classnames';

interface DualRangeSliderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  min: string | number;
  max: string | number;
  from: string | number;
  to: string | number;
  onChange?: (value: { from: string | number; to: string | number }) => void;
}

const DualRangeSlider = React.forwardRef<HTMLDivElement, DualRangeSliderProps>(
  (
    { min, max, from, to, className, onChange, ...props },
    ref: MutableRefObject<HTMLInputElement>
  ) => {
    const [controlledValue, setControlledValue] = useState({ from, to });

    useEffect(() => {
      setControlledValue({ from, to });
    }, [from, to]);

    const handleInput = useCallback(
      (key: string, value: number) => {
        const val = {
          ...controlledValue,
          [key]: value,
        };
        setControlledValue(val);
        onChange(val);
      },
      [controlledValue]
    );

    const getPercent = (value: number | string) =>
      ((Number(value) - Number(min)) * 100) / (Number(max) - Number(min));

    return (
      <div
        className={cx('dual-range-slider', className && className)}
        ref={ref}
        {...props}
      >
        <input
          type="range"
          value={controlledValue.from}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = parseInt(e.currentTarget.value);
            if (value <= Number(controlledValue.to)) handleInput('from', value);
            e.preventDefault();
          }}
          min={min}
          max={max}
          style={{
            backgroundSize: `${getPercent(controlledValue.from)}% 100%`,
          }}
        />
        <input
          type="range"
          value={controlledValue.to}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const value = parseInt(e.currentTarget.value);
            if (value >= Number(controlledValue.from)) handleInput('to', value);
            e.preventDefault();
          }}
          min={min}
          max={max}
          style={{
            backgroundSize: `${getPercent(controlledValue.to)}% 100%`,
          }}
        />
      </div>
    );
  }
);

DualRangeSlider.displayName = 'DualRangeSlider';
DualRangeSlider.defaultProps = {
  min: 0,
  max: 100,
  from: 0,
  to: 100,
};

export default DualRangeSlider;
